import type { Route, RoutePoint, RouteStorageProvider, StorageResult, StorageConfig } from '../../types/storage.js';
import { LocalStorageProvider } from './LocalStorageProvider.js';
import { SupabaseStorageProvider } from './SupabaseStorageProvider.js';

export interface SessionFunctions {
  getAccessToken: () => Promise<string | null>;
  refreshSession: () => Promise<boolean>;
}

export class RouteStorageManager {
  private provider: RouteStorageProvider;
  private config: StorageConfig;
  private sessionFunctions?: SessionFunctions;

  constructor(config: StorageConfig, sessionFunctions?: SessionFunctions) {
    this.config = config;
    this.sessionFunctions = sessionFunctions;
    this.provider = this.createProvider(config);
  }

  private createProvider(config: StorageConfig): RouteStorageProvider {
    switch (config.type) {
      case 'supabase':
        return new SupabaseStorageProvider(
          config.endpoint, 
          config.apiKey,
          this.sessionFunctions?.getAccessToken
        );
      case 'localStorage':
        return new LocalStorageProvider();
      default:
        console.warn(`Unknown storage type: ${config.type}, falling back to localStorage`);
        return new LocalStorageProvider();
    }
  }

  // Switch storage provider at runtime
  switchProvider(config: StorageConfig): void {
    this.config = config;
    this.provider = this.createProvider(config);
  }

  getCurrentConfig(): StorageConfig {
    return { ...this.config };
  }

  // Route management methods
  async saveRoute(route: Omit<Route, 'id' | 'created'>): Promise<StorageResult<Route>> {
    return this.provider.saveRoute(route);
  }

  async getRoute(id: string, includeFullData: boolean = false): Promise<StorageResult<Route>> {
    return this.provider.getRoute(id, includeFullData);
  }

  async getRoutes(userId: string): Promise<StorageResult<Route[]>> {
    return this.provider.getRoutes(userId);
  }

  async deleteRoute(id: string): Promise<StorageResult<boolean>> {
    return this.provider.deleteRoute(id);
  }

  async updateRoute(id: string, route: Partial<Route>): Promise<StorageResult<Route>> {
    return this.provider.updateRoute(id, route);
  }

  async updateRoutePoint(routeId: string, pointId: string, pointData: Partial<RoutePoint>): Promise<StorageResult<RoutePoint>> {
    return this.provider.updateRoutePoint(routeId, pointId, pointData);
  }

  // Full route data methods
  async saveFullRouteData(routeId: string, fullData: any): Promise<StorageResult<any>> {
    return this.provider.saveFullRouteData(routeId, fullData);
  }

  async getFullRouteData(routeId: string): Promise<StorageResult<any>> {
    return this.provider.getFullRouteData(routeId);
  }

  async updateFullRouteData(routeId: string, fullData: any): Promise<StorageResult<any>> {
    return this.provider.updateFullRouteData(routeId, fullData);
  }

  // Check if a route name already exists for a user (excluding a specific route ID for updates)
  async checkRouteNameExists(userId: string, routeName: string, excludeRouteId?: string): Promise<StorageResult<boolean>> {
    try {
      const result = await this.provider.getRoutes(userId);
      
      if (!result.success || !result.data) {
        return {
          success: false,
          error: result.error || 'Failed to fetch user routes'
        };
      }

      const nameExists = result.data.some(route => 
        route.name.toLowerCase() === routeName.toLowerCase() && 
        route.id !== excludeRouteId
      );

      return {
        success: true,
        data: nameExists
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Utility methods
  async testConnection(): Promise<StorageResult<boolean>> {
    try {
      // For Supabase, try to get routes with a valid UUID (will return empty array if no routes exist)
      // Using a well-formed UUID that won't exist but is valid syntax
      const testUserId = '00000000-0000-4000-8000-000000000000';
      const result = await this.provider.getRoutes(testUserId);
      return {
        success: result.success,
        data: true,
        error: result.error
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Connection test failed'
      };
    }
  }

  // Helper method to get a default user ID (you can replace this with actual user management)
  static getDefaultUserId(): string {
    // Check if we have a valid UUID stored
    let userId = localStorage.getItem('cycling-buddy-user-id');
    
    // Validate UUID format (UUID v4 pattern)
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    
    if (!userId || !uuidPattern.test(userId)) {
      // Generate a simple UUID v4-like string
      userId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
      localStorage.setItem('cycling-buddy-user-id', userId);
    }
    return userId;
  }
}
